// Supabase magic-link callback page.
//
// The flow:
//   1. User taps "Send sign-in link" in Settings -> Account.
//   2. Supabase emails them a link to `${origin}/auth/supabase/callback?...`.
//   3. They open it; this page processes the URL parameters and
//      exchanges them for a session.
//   4. We show a "Finishing sign-in…" spinner and then navigate back
//      to /map (or wherever the user came from).
//
// PKCE flow gotcha (fixed 2026-05-11):
// ------------------------------------
// Our supabase client uses `flowType: 'pkce'`, which puts a
// `?code=<auth-code>` in the callback URL. Supabase JS does NOT auto-
// exchange that code from `detectSessionInUrl: true` — that flag only
// handles the legacy `#access_token=...` fragment flow. For PKCE you
// MUST explicitly call `exchangeCodeForSession(href)`. Without that
// the user lands here, `getSession()` returns null, and they see
// "No session in callback URL".
//
// The legacy fragment flow ALSO needs handling for backward compat,
// so we check for both: PKCE `?code=` first, then fragment hash.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';

export default function SupabaseAuthCallback() {
  const nav = useNavigate();
  const [state, setState] = useState<'pending' | 'ok' | 'error'>('pending');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!supabase) {
        setState('error');
        setError('Supabase not configured');
        return;
      }
      try {
        const href = typeof window !== 'undefined' ? window.location.href : '';
        const url = new URL(href);

        // 1. Check for Supabase OAuth error in the URL first — expired
        //    link, denied auth, etc. show a useful message rather than
        //    the generic "No session".
        const urlError = url.searchParams.get('error')
          ?? url.hash.match(/error=([^&]+)/)?.[1];
        const urlErrorDesc = url.searchParams.get('error_description')
          ?? url.hash.match(/error_description=([^&]+)/)?.[1];
        if (urlError) {
          setState('error');
          setError(
            urlErrorDesc
              ? decodeURIComponent(urlErrorDesc.replace(/\+/g, ' '))
              : urlError,
          );
          return;
        }

        // 2. PKCE flow: `?code=<auth-code>` in query string. Must call
        //    exchangeCodeForSession explicitly.
        const code = url.searchParams.get('code');
        if (code) {
          const { data, error: exchErr } = await supabase.auth.exchangeCodeForSession(href);
          if (cancelled) return;
          if (exchErr) {
            setState('error');
            // Common: "code verifier should be non-empty" when the user
            // opens the email link in a different browser/device than
            // where they requested it. Give a clearer message.
            const msg = exchErr.message?.toLowerCase().includes('code verifier')
              ? 'Please open this link in the same browser where you requested it.'
              : exchErr.message;
            setError(msg);
            return;
          }
          if (data.session) {
            await useAuthStore.getState().refreshProfile();
            setState('ok');
            setTimeout(() => nav('/map', { replace: true }), 600);
            return;
          }
        }

        // 3. Legacy fragment flow: `#access_token=...&refresh_token=...`
        //    in the URL hash. Supabase JS auto-detects this when the
        //    client initializes (we set `detectSessionInUrl: true`).
        //    By the time we get here, the session should already be in
        //    storage — just confirm it via getSession.
        const { data: sess, error: sessErr } = await supabase.auth.getSession();
        if (cancelled) return;
        if (sessErr) {
          setState('error');
          setError(sessErr.message);
          return;
        }
        if (sess.session) {
          await useAuthStore.getState().refreshProfile();
          setState('ok');
          setTimeout(() => nav('/map', { replace: true }), 600);
          return;
        }

        // 4. Nothing in the URL, nothing in storage — the link was
        //    probably already consumed (e.g. refresh of the callback
        //    page after a successful exchange). Bounce back to the
        //    map; if there's a session it'll work, if not the user
        //    can request another link.
        setState('error');
        setError('No session in callback URL. The link may have already been used or expired — please request a new sign-in link.');
      } catch (e: any) {
        if (cancelled) return;
        setState('error');
        setError(e?.message ?? String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [nav]);

  return (
    <div className="absolute inset-0 bg-cockpit-900 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-cockpit-800 border border-cockpit-700 rounded-2xl px-5 py-6 space-y-3 text-center">
        <img
          src="/icon-192.png"
          alt="Fyrefly"
          className="h-14 w-14 mx-auto rounded-xl"
        />
        {state === 'pending' && (
          <>
            <div className="text-lg text-cockpit-100">Finishing sign-in…</div>
            <p className="text-[11px] text-cockpit-300">
              One moment while we verify your link.
            </p>
          </>
        )}
        {state === 'ok' && (
          <>
            <div className="text-lg text-cockpit-100">Signed in ✓</div>
            <p className="text-[11px] text-cockpit-300">Redirecting…</p>
          </>
        )}
        {state === 'error' && (
          <>
            <div className="text-lg text-cockpit-100">Sign-in failed</div>
            <p className="text-[11px] text-red-400">{error}</p>
            <button
              onClick={() => nav('/settings', { replace: true })}
              className="bg-ember-600 hover:bg-ember-500 text-white text-sm rounded-lg px-3 py-1.5"
            >
              Back to Settings
            </button>
          </>
        )}
      </div>
    </div>
  );
}
